import React, { useEffect, useRef, useState } from "react";
import { TiThMenu } from "react-icons/ti";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../redux/auth/authActions";
import { Link, useNavigate } from "react-router-dom";
import { FaMoon, FaSun, FaUser } from "react-icons/fa";
import { MdLiveHelp } from "react-icons/md";
import { IoLogOutSharp, IoSettings } from "react-icons/io5";
import {
  toggleDarkMode,
  toggleLanguage,
} from "../../../redux/settings/settingsActions";
import { useUser, useUserRoles } from "../../../authentication/authentication";
import api from "../../../api";
import { motion } from "framer-motion";
import SearchBar from "../../Search/SearchBar";
import { LanguagesIcon, Subscript } from "lucide-react";
import NotificationSide from "./NotificationSide";
import CustomSelect from "../../CustomSelect";
//const socket = io(nodeApi); // Ensure this URL is correct

import { NavLink } from "react-router-dom";
import errorChecker from "../../../utils/errorChecker";
import { arClass } from "../../../utils/useArClass";
import useWindowSize from "../../../utils/useWindowSize";
import { isAdminRole } from "../../../utils/adminChecker";
const ProfileDropdown = ({
  defaultLanguage,
  handleLogout,
  darkMode,
  profileImg,
  onClose, // Add onClose prop
}) => {
  // Texts in en, fr, ar
  const texts = {
    en: {
      profile: "Profile",
      appearance: "Appearance",
      subscription: "Subscription",
      requests: "Requests",
      help: "Help",
      logout: "Logout",
    },
    fr: {
      profile: "Profil",
      subscription: "Abonnement",
      appearance: "Apparence",
      requests: "Demandes",
      help: "Aide",
      logout: "Se déconnecter",
    },
    ar: {
      profile: "الملف الشخصي",
      subscription: "الاشتراك",
      appearance: "المظهر",
      requests: "الطلبات",
      help: "مساعدة",
      logout: "تسجيل الخروج",
    },
  };

  const t = texts[defaultLanguage] || texts.en;

  const links = [
    { name: t.profile, url: "/profile", icon: null },
    { name: t.subscription, url: "/subscription", icon: null },
    { name: t.appearance, url: "/settings", icon: null },
    
    { name: t.help, url: "/create-request/HELP_REQUEST", icon: null },
  ];
  const roles = useUserRoles();
  isAdminRole(roles) ? links.push({ name: t.requests, url: "/my-requests", icon: null }) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -6, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -6, scale: 0.98 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
      className="w-full"
    >
      {/* Header */}
      <div
        className={arClass(
          `p-4 border-b ${darkMode ? "border-gray-700" : "border-gray-200"}`
        )}
      >
        <div className={arClass("flex items-center justify-between mb-3")}>
          <h3
            className={arClass(
              `font-semibold max-sm:text-sm ${
                darkMode ? "text-white" : "text-gray-900"
              }`
            )}
          >
            {profileImg ? (
              <div
                className={arClass("flex items-center gap-3 max-sm:text-xs")}
              >
                <motion.img
                  src={profileImg}
                  alt="Profile"
                  className={arClass(
                    "w-10 h-10 object-cover rounded-full info-color  max-sm:text-xs"
                  )}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                />
                <span>{t.profile}</span>
              </div>
            ) : (
              t.profile
            )}
          </h3>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose} // Use onClose here
            className={arClass(
              `p-1 rounded-full max-sm:text-xs ${
                darkMode
                  ? "hover:bg-red-700/60 text-red-400 bg-red-500/20"
                  : "hover:bg-red-100 text-red-500 bg-red-500/20"
              }`
            )}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </motion.button>
        </div>
      </div>

      {/* Content */}
      <div className={arClass("max-h-76 overflow-y-auto p-2")}>
        {links.map((link, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.03 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={arClass("")}
          >
            <NavLink
              to={link.url}
              onClick={onClose} // Close dropdown when clicking a link
              className={arClass(
                `flex items-center gap-3 p-3 max-sm:p-2 rounded-lg text-sm w-full transition-all duration-200 !mt-1 max-sm:text-xs  ${
                  darkMode
                    ? "!text-white hover:bg-gray-500/10"
                    : "!text-black hover:bg-gray-200/50"
                }`
              )}
            >
              {link.icon &&
                React.cloneElement(link.icon, {
                  className: arClass(
                    `text-sm ${darkMode ? "text-white" : "text-black"}`
                  ),
                })}
              <span
                className={arClass("font-medium max-sm:text-xs text-black")}
              >
                {link.name}
              </span>
            </NavLink>
          </motion.div>
        ))}

        {/* Logout button */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.03 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <button
            onClick={() => {
              onClose(); // Close dropdown first
              handleLogout(); // Then handle logout
            }}
            className={arClass(
              `flex items-center gap-3 px-3 py-3 mx-1 rounded-lg text-sm w-full transition-all duration-200 mt-1 max-sm:text-xs max-sm:p-2 ${
                darkMode
                  ? "text-red-400 hover:bg-red-900/20"
                  : "text-red-600 hover:bg-red-50"
              }`
            )}
          >
            <span className={arClass("font-medium max-sm:text-xs")}>
              {t.logout}
            </span>
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
};

const ProfileButtonWithDropdown = ({
  defaultLanguage,
  handleLogout,
  darkMode,
  profileImg,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both dropdown and button
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    // Handle escape key press
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && isDropdownOpen) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isDropdownOpen]);

  const handleCloseDropdown = () => {
    setIsDropdownOpen(false);
  };

  return (
    <div className="relative">
      {/* Profile Button */}
      <motion.button
        ref={buttonRef}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="focus:outline-none"
        aria-label="Toggle profile dropdown"
        aria-expanded={isDropdownOpen}
      >
        <motion.img
          src={profileImg}
          alt="Profile"
          className="w-10 h-10 object-cover rounded-full info-color  border-2 border-transparent hover:border-blue-500 transition-colors"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        />
      </motion.button>

      {/* Dropdown */}
      {isDropdownOpen && (
        <motion.div
          ref={dropdownRef}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.2 }}
          className={arClass(
            "absolute right-0 mt-2 w-64 rounded-lg shadow-lg z-50 overflow-hidden"
          )}
          style={{
            backgroundColor: darkMode ? "#1f2937" : "white",
            border: darkMode ? "1px solid #374151" : "1px solid #e5e7eb",
          }}
          role="menu"
          aria-orientation="vertical"
        >
          <ProfileDropdown
            defaultLanguage={defaultLanguage}
            handleLogout={handleLogout}
            darkMode={darkMode}
            profileImg={profileImg}
            onClose={handleCloseDropdown} // Pass close function
          />
        </motion.div>
      )}
    </div>
  );
};

const languageOptions = {
  
  fr:[
    { label: "Français", value: "fr",code: "fr-FR" },
     { label: "Anglais", value: "en",code: "en-US" },
    { label: "Arabe", value: "ar",code: "ar-AR" },
  ],
  en:[
    
    { label: "French", value: "fr",code: "fr-FR" },
    { label: "English", value: "en",code: "en-US" },
    { label: "Arabic", value: "ar",code: "ar-AR" },
  ],
  ar:[
    
    { label: "الفرنسية", value: "fr",code: "fr-FR" },
    { label: "الإنجليزية", value: "en",code: "en-US" },
    { label: "العربية", value: "ar",code: "ar-AR" },
  ],
  
};


export default function AdminNav({
  isSideNavVisible,
  toggleSideNavVisibility,
}) {
  const dispatch = useDispatch();

  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage
  );
  const localUser = useUser();
  const [profileImg, setProfileImg] = useState(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isNotificationDropdownVisible, setIsNotificationDropdownVisible] =
    useState(false);
  const dropdownRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  const navigate = useNavigate();
  const [userLoading, setUserLoading] = useState(true);
  const isRtl = defaultLanguage === "ar";
  const bodyWidth = useWindowSize().width;
  const mobileView = bodyWidth < 640;
  const desktopView = bodyWidth >= 1280;
  const navPositionStyle = isRtl
    ? {
        right: mobileView
          ? bodyWidth - 290 + "px"
          : desktopView
          ? bodyWidth - 620 + "px"
          : bodyWidth - 460 + "px",
      }
    : {
        left: mobileView
          ? bodyWidth - 290 + "px"
          : desktopView
          ? bodyWidth - 620 + "px"
          : bodyWidth - 460 + "px",
      };
  useEffect(() => {
    const img = localUser.profileImage;
    setProfileImg(img);
    setUserLoading(false);
  }, [localUser.profileImage]);

  const texts = {
    en: {
      welcome: "Welcome Back",
      subtitle: "Sign in to access your medical dashboard",
      email: "Email Address",
      password: "Password",
      rememberMe: "Remember me",
      forgotPassword: "Forgot password?",
      signIn: "Sign In",
      orContinue: "Or continue with",
      continueGoogle: "Continue with Google",
      twoFactorTitle: "Two-Factor Authentication",
      twoFactorSubtitle: "Enter the 6-digit code from your authenticator app",
      verifyCode: "Verify Code",
      setup2FA: "Set Up Two-Factor Authentication",
      scanQR: "Scan this QR code with your authenticator app",
      manualEntry: "Or enter this secret key manually:",
      copied: "Copied!",
      copy: "Copy",
      scannedQR: "I've Scanned the QR Code",
      secureTitle: "Secure Medical Dashboard",
      secureSubtitle:
        "Access your patient records and medical tools with enterprise-grade security",
      encryption: "End-to-end encryption",
      hipaa: "HIPAA compliant",
      attempts: "Attempts",
      locked: "Account temporarily locked. Please contact support.",
      tryAgain: "Too many attempts. Try again in",
      seconds: "seconds",
      footer: "© 2024 Raqib Medical. All rights reserved.",
      warning: "Warning your password now is visible to others!",
      info: "You can see your password by clicking the eye icon.",
      toggleTheme: "Toggle theme",
      profile: "Profile",
      appearance: "Appearance",
      help: "Help",
      logout: "Logout",
      language: "Language",
      arabic: "Arabic",
      english: "English",
      french: "French",
    },
    fr: {
      welcome: "Bon retour",
      subtitle: "Connectez-vous pour accéder à votre tableau de bord médical",
      email: "Adresse e-mail",
      password: "Mot de passe",
      rememberMe: "Se souvenir de moi",
      forgotPassword: "Mot de passe oublié?",
      signIn: "Se connecter",
      orContinue: "Ou continuer avec",
      continueGoogle: "Continuer avec Google",
      twoFactorTitle: "Authentification à deux facteurs",
      twoFactorSubtitle:
        "Entrez le code à 6 chiffres de votre application d'authentification",
      verifyCode: "Vérifier le code",
      setup2FA: "Configurer l'authentification à deux facteurs",
      scanQR: "Scannez ce code QR avec votre application d'authentification",
      manualEntry: "Ou entrez cette clé secrète manuellement:",
      copied: "Copié!",
      copy: "Copier",
      scannedQR: "J'ai scanné le code QR",
      secureTitle: "Tableau de Bord Médical Sécurisé",
      secureSubtitle:
        "Accédez à vos dossiers patients et outils médicaux avec une sécurité de niveau entreprise",
      encryption: "Chiffrement de bout en bout",
      hipaa: "Conforme HIPAA",
      attempts: "Tentatives",
      locked:
        "Compte temporairement verrouillé. Veuillez contacter le support.",
      tryAgain: "Trop de tentatives. Réessayez dans",
      seconds: "secondes",
      footer: "© 2024 Raqib Medical. Tous droits réservés.",
      warning:
        "Attention, votre mot de passe est maintenant visible par les autres !",
      info: "Vous pouvez voir votre mot de passe en cliquant sur l'icône en forme d'œil.",
      toggleTheme: "Changer le thème",
      profile: "Profil",
      appearance: "Apparence",
      help: "Aide",
      logout: "Se déconnecter",
      language: "Langue",
      arabic: "Arabe",
      english: "Anglais",
      french: "Français",
    },
    ar: {
      welcome: "مرحباً بعودتك",
      subtitle: "سجل الدخول للوصول إلى لوحة التحكم الطبية",
      email: "عنوان البريد الإلكتروني",
      password: "كلمة المرور",
      rememberMe: "تذكرني",
      forgotPassword: "نسيت كلمة المرور؟",
      signIn: "تسجيل الدخول",
      orContinue: "أو تابع باستخدام",
      continueGoogle: "تابع باستخدام جوجل",
      twoFactorTitle: "المصادقة الثنائية",
      twoFactorSubtitle:
        "أدخل الرمز المكون من 6 أرقام من تطبيق المصادقة الخاص بك",
      verifyCode: "تحقق من الرمز",
      setup2FA: "إعداد المصادقة الثنائية",
      scanQR: "امسح رمز QR هذا باستخدام تطبيق المصادقة الخاص بك",
      manualEntry: "أو أدخل المفتاح السري يدوياً:",
      copied: "تم النسخ!",
      copy: "نسخ",
      scannedQR: "لقد مسحت رمز QR",
      secureTitle: "لوحة التحكم الطبية الآمنة",
      secureSubtitle:
        "الوصول إلى سجلات المرضى والأدوات الطبية بأمان على مستوى المؤسسة",
      encryption: "التشفير من طرف إلى طرف",
      hipaa: "متوافق مع HIPAA",
      attempts: "محاولات",
      locked: "الحساب مؤقتاً مغلق. يرجى الاتصال بالدعم.",
      tryAgain: "محاولات كثيرة. حاول مرة أخرى بعد",
      seconds: "ثواني",
      footer: "© 2024 راقب الطبي. جميع الحقوق محفوظة.",
      warning: "تحذير! كلمة المرور الخاصة بك الآن مرئية للآخرين!",
      info: "يمكنك رؤية كلمة المرور الخاصة بك بالنقر على أيقونة العين.",
      toggleTheme: "تبديل السمة",
      profile: "الملف الشخصي",
      appearance: "المظهر",
      help: "مساعدة",
      logout: "تسجيل الخروج",
      language: "اللغة",
      arabic: "العربية",
      english: "الإنجليزية",
      french: "الفرنسية",
    },
  };

  const t = texts[defaultLanguage];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownVisible(false);
      }
      if (
        notificationDropdownRef.current &&
        !notificationDropdownRef.current.contains(event.target)
      ) {
        setIsNotificationDropdownVisible(false);
      }
    };

    if (isDropdownVisible || isNotificationDropdownVisible) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isDropdownVisible, isNotificationDropdownVisible]);

  // Handlers
  const handleOpenSideNav = () => {
    toggleSideNavVisibility(isSideNavVisible);
  };

  const handleLogout = () => {
    api
      .post("auth/logout/")
      .then(() => {
        localStorage.removeItem("id");
    localStorage.removeItem("doctorId");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("role");
    localStorage.removeItem("profileImage");
    localStorage.removeItem("clinicLogo");
        dispatch(logout());
        navigate("/login");
      })
      .catch((e) => {
        errorChecker(e, dispatch);
      });
  };

  const handleChangeLanguge = (lang) => {
    dispatch(toggleLanguage(lang));
  };

  const toggleTheme = () => {
    dispatch(toggleDarkMode());
  };

  const className =
    defaultLanguage === "ar"
      ? `pr-64 max-xl:pr-24 max-sm:pr-0 [direction:rtl] [text-align:right] `
      : `pl-64 max-xl:pl-24 max-sm:pl-0`;

  // Render
  return (
    <nav
      className={`fixed top-0 z-[1000] flex items-center justify-between h-16 w-full border-b ${
        darkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
      } ${className}`}
    >
      <button
        className={arClass(
          `open hidden items-center p-1 rounded-md text-xl absolute mx-3 max-sm:flex ${
            darkMode
              ? "!text-gray-300 hover:!text-blue-400 hover:!bg-gray-700 !bg-gray-900 !border-gray-300 border"
              : "!text-gray-600 hover:!text-blue-600 hover:!bg-blue-50 !bg-gray-200 !border-gray-300 border"
          }`
        )}
        onClick={handleOpenSideNav}
      >
        <TiThMenu />
      </button>
      <div className={arClass("flex items-center  w-full absolute -right-4")}>
        {/* Global search: */}
        <SearchBar className="" darkMode={darkMode} />
      </div>

      <div
        className={arClass(`flex items-center gap-6  max-sm:gap-3 absolute`)}
        style={navPositionStyle}
      >
        <div className={arClass("w-36 max-sm:w-28")}>
          <CustomSelect
                            options={Object.values(languageOptions[defaultLanguage])
                              .map((opt) => ({ label: opt.label, value: opt.value, code: opt.code }))
                            }
                            value={defaultLanguage}
                            onChange={(e) =>
                              handleChangeLanguge(e.target.value)
                            }
                            allowCreate={false}
                            allowSearch={false}
                            className="max-sm:text-sm !rounded-full !p-2"
                          />
        
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={arClass(
            `flex items-center gap-2 max-sm:h-9 max-sm:w-9 max-sm:text-sm justify-center  transition-colors text-xl rounded-full !p-2  !shadow-none info-color main-text-color ${
              darkMode
                ? "!text-gray-300 hover:!text-blue-400 hover:!bg-gray-700 !bg-gray-900 !border-gray-300 border"
                : "!text-gray-600 hover:!text-blue-600 hover:!bg-blue-50 !bg-gray-200 !border-gray-300 border"
            }`
          )}
          type="button"
          onClick={toggleTheme}
          title={
            darkMode
              ? defaultLanguage === "ar"
                ? "تبديل السمة"
                : defaultLanguage === "fr"
                ? "Changer le thème"
                : "Toggle theme"
              : defaultLanguage === "ar"
              ? "تبديل السمة"
              : defaultLanguage === "fr"
              ? "Changer le thème"
              : "Toggle theme"
          }
        >
          {darkMode ? (
            <FaSun className="text-lg" />
          ) : (
            <FaMoon className="text-lg" />
          )}
        </motion.button>

        <NotificationSide userId={localUser.id} />

        <div
          className={arClass("flex justify-center items-center")}
          ref={dropdownRef}
        >
          <ProfileButtonWithDropdown
            defaultLanguage={defaultLanguage}
            handleLogout={handleLogout}
            darkMode={darkMode} // or false
            profileImg={profileImg}
          />
        </div>
      </div>
    </nav>
  );
}
